import React, { createContext, useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchBootstrap } from "../../services/api";
import { mapBootstrapData } from "../../services/bootstrapMapper";
import { EMPTY_BOOTSTRAP_STATE, mergeBootstrapState } from "../../services/defaultBootstrapState";
import { authApi, getAccessToken, wishlistApi } from "../../services/api";
import { getContactsForSubRegister } from "../../Utils/partnerContact";

export const MyContext = createContext();

export const CAR_SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "year_desc", label: "Year: Newest" },
  { value: "year_asc", label: "Year: Oldest" },
  { value: "km_asc", label: "Km: Low to High" },
  { value: "km_desc", label: "Km: High to Low" },
];

const isCarCertified = (car) => Boolean(car?.inspection?.isCertified);

const sortCars = (cars, sortBy) => {
  const list = [...cars];
  switch (sortBy) {
    case "price_asc":
      return list.sort((a, b) => (a.price || 0) - (b.price || 0));
    case "price_desc":
      return list.sort((a, b) => (b.price || 0) - (a.price || 0));
    case "year_asc":
      return list.sort((a, b) => (a.year || 0) - (b.year || 0));
    case "year_desc":
      return list.sort((a, b) => (b.year || 0) - (a.year || 0));
    case "km_asc":
      return list.sort((a, b) => (a.km_driven || 0) - (b.km_driven || 0));
    case "km_desc":
      return list.sort((a, b) => (b.km_driven || 0) - (a.km_driven || 0));
    default:
      return list.sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : Number(a.id) || 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : Number(b.id) || 0;
        return bTime - aTime;
      });
  }
};

export const MyProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(EMPTY_BOOTSTRAP_STATE);
  const [dataSource, setDataSource] = useState("api");
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const cached = localStorage.getItem("currentUser");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [userLoading, setUserLoading] = useState(() => Boolean(getAccessToken()));

  const refreshCurrentUser = useCallback(async () => {
    if (!getAccessToken()) {
      setCurrentUser(null);
      setUserLoading(false);
      return null;
    }
    setUserLoading(true);
    try {
      const res = await authApi.me();
      setCurrentUser(res.user);
      localStorage.setItem("currentUser", JSON.stringify(res.user));
      return res.user;
    } catch {
      setCurrentUser(null);
      localStorage.removeItem("currentUser");
      return null;
    } finally {
      setUserLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCurrentUser();
  }, [refreshCurrentUser]);

  const defaultFilters = {
    brand_ids: [],
    model_ids: [],
    fuel_type_ids: [],
    color_ids: [],
    feature_ids: [],
    body_type_ids: [],
    transmission_ids: [],
    seats_ids: [],
    year_min: 2010,
    year_max: 2025,
    tire_min: 0,
    tire_max: 100,
    price_min: 0,
    price_max: 10000000,
    km_min: 0,
    km_max: 1000000,
    search: "",
    certified_only: false,
  };

  const [filters, setFilters] = useState(defaultFilters);
  const [sortBy, setSortBy] = useState("newest");

  const [wishlistIds, setWishlistIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("wishlistIds") || "[]");
    } catch {
      return [];
    }
  });

  const [compareIds, setCompareIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("compareIds") || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("compareIds", JSON.stringify(compareIds));
  }, [compareIds]);

  useEffect(() => {
    const loadWishlist = async () => {
      if (!getAccessToken()) return;
      try {
        const local = JSON.parse(localStorage.getItem("wishlistIds") || "[]");
        if (local.length) {
          await wishlistApi.sync(local);
        }
        const res = await wishlistApi.list();
        const ids = res.data || [];
        setWishlistIds(ids);
        localStorage.setItem("wishlistIds", JSON.stringify(ids));
      } catch {
        /* keep local ids */
      }
    };
    if (!userLoading && currentUser) {
      loadWishlist();
    }
  }, [currentUser, userLoading]);

  useEffect(() => {
    localStorage.setItem("wishlistIds", JSON.stringify(wishlistIds));
  }, [wishlistIds]);

  useEffect(() => {
    let active = true;

    const loadBootstrap = async () => {
      try {
        const res = await fetchBootstrap();
        if (!active) return;
        const mapped = mapBootstrapData(res.data || {});
        setData(mergeBootstrapState(mapped));
        setDataSource("api");
      } catch (err) {
        console.error("Bootstrap load failed:", err);
        if (active) {
          setData(EMPTY_BOOTSTRAP_STATE);
          setDataSource("error");
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadBootstrap();

    const onReload = () => {
      loadBootstrap();
    };
    window.addEventListener("carsinfinite:bootstrap-reload", onReload);

    return () => {
      active = false;
      window.removeEventListener("carsinfinite:bootstrap-reload", onReload);
    };
  }, []);

  useEffect(() => {
    const getArray = (key) =>
      searchParams.get(key)
        ? searchParams.get(key).split(",").map(Number)
        : [];

    setFilters((prev) => ({
      ...prev,
      brand_ids: getArray("brand"),
      model_ids: getArray("model"),
      fuel_type_ids: getArray("fuel"),
      color_ids: getArray("color"),
      feature_ids: getArray("feature"),
      body_type_ids: getArray("body"),
      transmission_ids: getArray("trans"),
      seats_ids: getArray("seats"),
      year_min: Number(searchParams.get("year_min")) || prev.year_min,
      year_max: Number(searchParams.get("year_max")) || prev.year_max,
      tire_min: Number(searchParams.get("tier_min")) || prev.tire_min,
      tire_max: Number(searchParams.get("tier_max")) || prev.tire_max,
      price_min: Number(searchParams.get("price_min")) || prev.price_min,
      price_max: Number(searchParams.get("price_max")) || prev.price_max,
      km_min: Number(searchParams.get("km_min")) || prev.km_min,
      km_max: Number(searchParams.get("km_max")) || prev.km_max,
      search: searchParams.get("q") || "",
      certified_only: searchParams.get("certified") === "1",
    }));
    setSortBy(searchParams.get("sort") || "newest");
  }, [searchParams]);

  const toggleFilter = (key, value) => {
    setFilters((prev) => {
      const exists = prev[key].includes(value);
      const updated = {
        ...prev,
        [key]: exists
          ? prev[key].filter((id) => id !== value)
          : [...prev[key], value],
      };
      updateURL(updated, sortBy);
      return updated;
    });
  };

  const applyRange = (rangeUpdate) => {
    const updated = { ...filters, ...rangeUpdate };
    setFilters(updated);
    updateURL(updated, sortBy);
  };

  const clearFilters = () => {
    setFilters({ ...defaultFilters, search: "" });
    setSortBy("newest");
    setSearchParams({});
  };

  const setSearch = (value) => {
    setFilters((prev) => {
      const updated = { ...prev, search: value };
      updateURL(updated, sortBy);
      return updated;
    });
  };

  const setCertifiedOnly = (value) => {
    setFilters((prev) => {
      const updated = { ...prev, certified_only: value };
      updateURL(updated, sortBy);
      return updated;
    });
  };

  const changeSortBy = (value) => {
    setSortBy(value);
    updateURL(filters, value);
  };

  const updateURL = (currentFilters, currentSort = sortBy) => {
    const params = {};
    if (currentFilters.brand_ids.length) params.brand = currentFilters.brand_ids.join(",");
    if (currentFilters.model_ids.length) params.model = currentFilters.model_ids.join(",");
    if (currentFilters.fuel_type_ids.length) params.fuel = currentFilters.fuel_type_ids.join(",");
    if (currentFilters.color_ids.length) params.color = currentFilters.color_ids.join(",");
    if (currentFilters.feature_ids.length) params.feature = currentFilters.feature_ids.join(",");
    if (currentFilters.body_type_ids.length) params.body = currentFilters.body_type_ids.join(",");
    if (currentFilters.transmission_ids.length) params.trans = currentFilters.transmission_ids.join(",");
    if (currentFilters.seats_ids.length) params.seats = currentFilters.seats_ids.join(",");
    if (currentFilters.year_min !== 2010) params.year_min = currentFilters.year_min;
    if (currentFilters.year_max !== 2025) params.year_max = currentFilters.year_max;
    if (currentFilters.tire_min !== 0) params.tier_min = currentFilters.tire_min;
    if (currentFilters.tire_max !== 100) params.tier_max = currentFilters.tire_max;
    if (currentFilters.price_min !== 0) params.price_min = currentFilters.price_min;
    if (currentFilters.price_max !== 10000000) params.price_max = currentFilters.price_max;
    if (currentFilters.km_min !== 0) params.km_min = currentFilters.km_min;
    if (currentFilters.km_max !== 1000000) params.km_max = currentFilters.km_max;
    if (currentFilters.search?.trim()) params.q = currentFilters.search.trim();
    if (currentFilters.certified_only) params.certified = "1";
    if (currentSort && currentSort !== "newest") params.sort = currentSort;
    setSearchParams(params);
  };

  const getContactsForCar = useCallback(
    (subRegisterId) =>
      getContactsForSubRegister(data.partners, subRegisterId, data.adminContact),
    [data.partners, data.adminContact]
  );

  const filteredCars = useMemo(() => {
    const filtered = data.cars.filter((car) => {
      if (filters.certified_only && !isCarCertified(car)) return false;
      if (filters.brand_ids.length && !filters.brand_ids.includes(car.brand_id)) return false;
      if (filters.model_ids.length && !filters.model_ids.includes(car.model_id)) return false;
      if (filters.fuel_type_ids.length && !filters.fuel_type_ids.includes(car.fuel_type_id)) return false;
      if (filters.color_ids.length && !filters.color_ids.includes(car.color_id)) return false;
      if (filters.body_type_ids.length && !filters.body_type_ids.includes(car.body_type_id)) return false;
      if (filters.transmission_ids.length && !filters.transmission_ids.includes(car.transmission_id)) return false;
      if (filters.seats_ids.length && !filters.seats_ids.includes(car.seats_id)) return false;
      if (filters.feature_ids.length) {
        const featureIds = Array.isArray(car.feature_ids) ? car.feature_ids : [];
        if (!filters.feature_ids.every((id) => featureIds.includes(id))) return false;
      }
      if (car.year < filters.year_min || car.year > filters.year_max) return false;
      if (car.tire_wear < filters.tire_min || car.tire_wear > filters.tire_max) return false;
      if (car.km_driven < filters.km_min || car.km_driven > filters.km_max) return false;
      if (car.price < filters.price_min || car.price > filters.price_max) return false;

      if (filters.search?.trim()) {
        const q = filters.search.trim().toLowerCase();
        const brandName = data.brands.find((b) => b.id === car.brand_id)?.name || "";
        const modelName = data.model.find((m) => m.id === car.model_id)?.name || "";
        const haystack = `${brandName} ${modelName} ${car.year} ${car.location || ""}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }

      return true;
    });

    return sortCars(filtered, sortBy);
  }, [data.cars, data.brands, data.model, filters, sortBy]);

  const certifiedCarCount = useMemo(
    () => data.cars.filter(isCarCertified).length,
    [data.cars]
  );

  const wishlistCars = useMemo(
    () => data.cars.filter((car) => wishlistIds.includes(Number(car.id))),
    [data.cars, wishlistIds]
  );

  const compareCars = useMemo(
    () =>
      compareIds
        .map((id) => data.cars.find((car) => car.id === Number(id)))
        .filter(Boolean),
    [data.cars, compareIds]
  );

  const isInWishlist = useCallback(
    (carId) => wishlistIds.includes(Number(carId)),
    [wishlistIds]
  );

  const isInCompare = useCallback(
    (carId) => compareIds.includes(Number(carId)),
    [compareIds]
  );

  const toggleWishlist = useCallback(async (carId) => {
    const id = Number(carId);
    const saved = wishlistIds.includes(id);

    if (getAccessToken()) {
      try {
        const res = saved ? await wishlistApi.remove(id) : await wishlistApi.add(id);
        setWishlistIds(res.data || []);
        return { saved: !saved };
      } catch {
        return { error: true };
      }
    }

    setWishlistIds((prev) => (saved ? prev.filter((x) => x !== id) : [...prev, id]));
    return { saved: !saved };
  }, [wishlistIds]);

  const toggleCompare = useCallback(
    (carId) => {
      const id = Number(carId);
      const max = data.compare_page?.maxCars || 3;

      if (compareIds.includes(id)) {
        setCompareIds((prev) => prev.filter((x) => x !== id));
        return { removed: true };
      }
      if (compareIds.length >= max) {
        return { full: true };
      }
      setCompareIds((prev) => [...prev, id]);
      return { added: true };
    },
    [compareIds, data.compare_page]
  );

  const removeFromCompare = useCallback((carId) => {
    const id = Number(carId);
    setCompareIds((prev) => prev.filter((x) => x !== id));
  }, []);

  const clearCompare = useCallback(() => {
    setCompareIds([]);
  }, []);

  const value = {
    filters,
    sortBy,
    toggleFilter,
    filteredCars,
    certifiedCarCount,
    applyRange,
    clearFilters,
    setSearch,
    setCertifiedOnly,
    changeSortBy,
    carSortOptions: CAR_SORT_OPTIONS,
    cars: data.cars,
    body_type: data.body_type,
    brands: data.brands,
    car_color: data.car_color,
    city: data.city,
    contact: data.contact,
    featuers: data.featuers,
    fuel_type: data.fuel_type,
    heroType: data.heroType,
    carousel: data.carousel,
    ads: data.ads,
    model: data.model,
    modified_cars: data.modified_cars,
    partners: data.partners,
    adminContact: data.adminContact,
    getContactsForCar,
    privacy: data.privacy,
    seats: data.seats,
    sellcar_form: data.sellcar_form,
    sellcar_page: data.sellcar_page,
    state: data.state,
    sub_register: data.sub_register,
    transmission: data.transmission,
    appPromo: data.appPromo,
    auth: data.auth,
    header: data.header,
    profile: data.profile,
    home_page: data.home_page,
    car_projects: data.car_projects,
    aboutUs: data.aboutUs,
    whychooseus: data.whychooseus,
    valuation_page: data.valuation_page,
    valuation_form: data.valuation_form,
    compare_page: data.compare_page,
    car_trust: data.car_trust,
    emi_page: data.emi_page,
    warranty_page: data.warranty_page,
    wishlistIds,
    compareIds,
    wishlistCars,
    compareCars,
    isInWishlist,
    isInCompare,
    toggleWishlist,
    toggleCompare,
    removeFromCompare,
    clearCompare,
    dataSource,
    dataLoading: loading,
    currentUser,
    setCurrentUser,
    refreshCurrentUser,
    userLoading,
  };

  return <MyContext.Provider value={value}>{children}</MyContext.Provider>;
};
